/* eslint-disable react-hooks/exhaustive-deps */
import * as Yup from "yup"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import {
  Close,
  Email,
  Lock,
  Person,
  Phone,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"
import { FormikTouched, FormikValues, useFormik } from "formik"
import { useEffect, useState } from "react"
import LoadingIndicator from "../../../app/components/loading-indicator"
import AppTextField from "../../../app/components/app-textfield"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { RootState } from "../../../app/store"
import {
  createUserThunk,
  fetchUserByIdThunk,
  removeCurrentUserAction,
  resetHandleUserStateAction,
  updateUserTnunk,
} from "../../handle-user/handle-user.slice"
import { closeUserFormDialogAct } from "../user-list.slice"

const USER_LEVELS = [
  { value: 0, label: "Administrador", description: "Acceso completo al panel" },
  { value: 1, label: "Referido", description: "Solo reportes de ventas" },
  { value: 4, label: "Vendedor", description: "Solo punto de venta" },
]

const initialValues = {
  name: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  level: 1,
}

export default function UserFormDialog() {
  const dispatch = useAppDispatch()
  const [showPassword, setShowPassword] = useState(false)
  const { userFormDialog } = useAppSelector((state: RootState) => state.users)
  const { loading, currentUser, created } = useAppSelector(
    (state: RootState) => state.handleUser,
  )

  const isEdit = userFormDialog?.mode === "edit"
  const userId = userFormDialog?.userId

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Ingresa el nombre").min(4, "Mínimo 4 caracteres"),
    lastName: Yup.string().required("Ingresa el apellido").min(4, "Mínimo 4 caracteres"),
    email: Yup.string().email("Correo inválido").required("El correo es obligatorio"),
    phone: Yup.string().min(6, "Mínimo 6 dígitos").required("El teléfono es obligatorio"),
    password: isEdit
      ? Yup.string().min(8, "Mínimo 8 caracteres")
      : Yup.string().min(8, "Mínimo 8 caracteres").required("La contraseña es obligatoria"),
    level: Yup.number().required("Selecciona un rol"),
  })

  const formik = useFormik({
    validationSchema,
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (isEdit && userId) {
        dispatch(updateUserTnunk({ dataUser: values, userId }))
      } else {
        dispatch(createUserThunk(values))
      }
    },
  })

  const closeDialog = () => {
    dispatch(closeUserFormDialogAct())
    dispatch(removeCurrentUserAction())
    dispatch(resetHandleUserStateAction())
    formik.resetForm()
    setShowPassword(false)
  }

  useEffect(() => {
    if (userFormDialog === undefined) return
    if (isEdit && userId) {
      dispatch(fetchUserByIdThunk(userId))
    } else {
      dispatch(removeCurrentUserAction())
      formik.resetForm()
    }
  }, [userFormDialog])

  useEffect(() => {
    if (currentUser !== undefined && isEdit) {
      const { email, lastName, name, phone, level } = currentUser
      formik.setValues({
        name,
        lastName,
        email,
        phone,
        password: "",
        level: level ?? 1,
      })
    }
  }, [currentUser, isEdit])

  useEffect(() => {
    if (created) {
      closeDialog()
    }
  }, [created])

  const getFieldError = (key: string): string => {
    const touched = (formik.touched as FormikTouched<FormikValues>)[key]
    const error = (formik.errors as FormikValues)[key]
    if (Boolean(touched) && typeof error === "string") {
      return error
    }
    return ""
  }

  const handleTextChange = ({ name, val }: { name: string; val: string }) => {
    formik.setFieldValue(name, val)
  }

  const selectedLevel = USER_LEVELS.find((l) => l.value === formik.values.level)

  return (
    <>
      <LoadingIndicator open={loading} onClose={() => {}} />
      <Dialog
        open={userFormDialog !== undefined}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Person color="primary" />
            <Typography variant="h6" component="span">
              {isEdit ? "Editar usuario" : "Nuevo usuario"}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isEdit
              ? "Actualiza los datos del usuario. Deja la contraseña vacía para mantener la actual."
              : "Completa los datos para registrar un nuevo usuario en el sistema."}
          </Typography>
          <IconButton
            aria-label="cerrar"
            onClick={closeDialog}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Información personal
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label="Nombre"
                  name="name"
                  value={formik.values.name}
                  onChange={handleTextChange}
                  required
                  hasError={Boolean(getFieldError("name"))}
                  error={getFieldError("name")}
                  startCompontent={<Person fontSize="small" color="action" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label="Apellido"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={handleTextChange}
                  required
                  hasError={Boolean(getFieldError("lastName"))}
                  error={getFieldError("lastName")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={handleTextChange}
                  required
                  hasError={Boolean(getFieldError("email"))}
                  error={getFieldError("email")}
                  startCompontent={<Email fontSize="small" color="action" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label="Teléfono"
                  name="phone"
                  value={formik.values.phone}
                  onChange={handleTextChange}
                  required
                  hasError={Boolean(getFieldError("phone"))}
                  error={getFieldError("phone")}
                  startCompontent={<Phone fontSize="small" color="action" />}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Acceso y permisos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={Boolean(getFieldError("level"))}
                  required
                >
                  <InputLabel>Rol del usuario</InputLabel>
                  <Select
                    name="level"
                    label="Rol del usuario"
                    value={formik.values.level}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {USER_LEVELS.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        <Box>
                          <Typography variant="body2">{level.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {level.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {getFieldError("level") && (
                    <FormHelperText>{getFieldError("level")}</FormHelperText>
                  )}
                  {selectedLevel && !getFieldError("level") && (
                    <FormHelperText>{selectedLevel.description}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <AppTextField
                  label={isEdit ? "Nueva contraseña (opcional)" : "Contraseña"}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={handleTextChange}
                  required={!isEdit}
                  hasError={Boolean(getFieldError("password"))}
                  error={getFieldError("password")}
                  startCompontent={<Lock fontSize="small" color="action" />}
                  endComponent={
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        aria-label={showPassword ? "ocultar contraseña" : "mostrar contraseña"}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={closeDialog} color="inherit" sx={{ cursor: "pointer" }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ cursor: "pointer", minWidth: 120 }}
            >
              {isEdit ? "Guardar cambios" : "Crear usuario"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}
